import React, { FC, MouseEventHandler } from 'react';
import { css } from '@emotion/react';
import { theme } from '$/components/Layout/theme';
import tw from 'twin.macro';
import {
  Button as SemanticButtonInternal,
  ButtonProps as SemanticButtonProps,
} from 'semantic-ui-react';
import { CloseIcon, PlusIcon, Spinner, TrashIcon } from '$/images/Icons';
import { Popup } from '../Popup';
import { ButtonT, useThemeContext } from '../ThemeContext';
import { readableColorOnBackground } from '$/shared/frontend/utils/tinyColor';
import { Portal } from '$/components/Portal';
import Loading from '$/components/Loading';
import { ThemeSettingsFragment } from '$/GraphQL/schema/types';

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  className?: string;
  inverted?: boolean;
  disabled?: boolean;
  theme?: ButtonT;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  icon?: React.FC<{ className?: string }>;
  iconRight?: boolean;
  as?: React.FC | string;
  onClick?: MouseEventHandler;
  id?: string;
  small?: boolean;
  noGrow?: boolean;
}

export const SemanticButton: FC<SemanticButtonProps> = ({ children, ...props }) => {
  return <SemanticButtonInternal {...props}>{children}</SemanticButtonInternal>;
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps & { underline?: boolean }>(
  (
    { as = 'button', theme, noGrow, small, underline, icon: Icon, iconRight, inverted, ...props },
    ref
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Btn = as as any;

    const etheme = useThemeContext();
    if (!theme) {
      theme = etheme.controls.button.basic;
    }

    if (inverted && theme) {
      theme = {
        fg: theme.bg,
        fgHover: theme.bg,
        bg: theme.fg,
        bgHover: theme.fg,
        border: theme.fg,
        borderHover: theme.fgHover,
      };
    }

    const hasBorder = theme.border || theme.borderHover;

    return (
      <Btn
        {...props}
        ref={ref}
        theme={theme}
        type={props.type || 'button'}
        className={`${noGrow && 'no-grow'} ${props.className}`}
        css={css`
          border: 1px solid transparent;
          border-radius: 3px;
          padding: ${!hasBorder ? '0' : '5px 12px'};

          transition:
            transform 200ms ease-out,
            color 200ms ease-out,
            border-color 200ms ease-out,
            opacity 0.25s ease-in-out !important;

          -webkit-tap-highlight-color: transparent;
          text-decoration: ${underline ? 'underline' : 'none'};
          ${theme?.border ? `border-color: ${theme.border};` : ''}
          ${theme?.fg ? `color: ${theme.fg}; & > svg { color: ${theme.fgIcon ?? theme.fg}; }` : ''}
          ${theme?.bg ? `background-color: ${theme.bg};` : ''}

          &:disabled {
            opacity: 0.7;
          }
          &:hover:not([disabled]) {
            ${theme?.fgHover
              ? `color: ${theme.fgHover}; & > svg { color: ${theme.fgIcon ?? theme.fgHover} !important; }`
              : ''}
            ${theme?.bgHover ? `background-color: ${theme.bgHover};` : ''}
            ${theme?.borderHover ? `border-color: ${theme.borderHover};` : ''}
            text-decoration: ${underline ? 'underline' : 'none'};
            transform: scale(1.025);
            &.no-grow {
              transform: unset;
            }
          }
          ${tw`
            flex items-center
            justify-center cursor-pointer appearance-none
            outline-none whitespace-nowrap
          `}
        `}
      >
        {Icon && !iconRight ? <Icon className='mr-1/2e h-1e' /> : null}
        {props.children}
        {Icon && iconRight ? <Icon className='ml-1/2e h-1e' /> : null}
      </Btn>
    );
  }
);

export const IconButton: FC<Omit<ButtonProps, 'icon'> & { icon: JSX.Element }> = ({
  icon,
  as,
  theme,
  ...props
}) => {
  const As = as ?? Button;

  if (!props.type) {
    props.type = 'button';
  }

  if (!theme) {
    theme = {
      // None is invalid but it has the outcome we want which is to look at the next CSS rule
      fg: 'none',
      fgHover: 'none',
      bg: 'none',
      bgHover: 'none',
      borderHover: 'none',
    };
  }

  if (props.inverted) {
    theme = { ...theme, fg: 'transparent' };
  }

  return (
    <As
      {...props}
      theme={theme}
      css={css`
        ${tw`
          flex-row items-center
          justify-center cursor-pointer appearance-none
          outline-none whitespace-nowrap p-0
        `}
        display: flex;
        svg,
        div {
          ${tw`transition-transform duration-100`}
          transform-origin: center;
          :disabled {
            opacity: 0.7;
          }
          &:hover:not([disabled]) {
            transform: scale(1.1);
          }
          &:active {
            transform: scale(1) translate(1px, 1px);
          }
        }
      `}
    >
      {icon}
    </As>
  );
};

export const Rounded: FC<ButtonProps> = ({ className, children, theme, inverted, ...props }) => {
  const etheme = useThemeContext();

  if (!theme) {
    theme = {
      ...etheme.controls.button.branded,
    };
  }

  return (
    <Button
      {...props}
      className={`${className}`}
      theme={theme}
      css={css`
        border-radius: 9999px;
        padding: 0.5rem 1rem;
        min-height: 2.4rem;
        font-weight: 400;
      `}
    >
      {children}
    </Button>
  );
};

export const BoxButton: FC<ButtonProps> = ({ className, children, theme, ...props }) => {
  const etheme = useThemeContext();

  if (!theme) {
    theme = {
      ...etheme.controls.button.bordered,
    };
  }

  return (
    <Button
      {...props}
      className={className}
      theme={theme}
      noGrow
      css={css`
        ${tw`border-2`}
        min-height: 2.4rem;
        font-weight: 400;
      `}
    >
      {children}
    </Button>
  );
};

export const RowButton: FC<ButtonProps & { submitting?: boolean }> = ({
  className,
  children,
  theme,
  submitting,
  ...props
}) => {
  const etheme = useThemeContext();

  if (!theme) {
    theme = {
      ...etheme.controls.button.row,
    };
  }

  return (
    <Button
      {...props}
      className={`row-button ${className}`}
      theme={theme}
      noGrow
      css={css`
        ${tw`flex inline-flex relative items-center whitespace-nowrap`}
        z-index: 1;
        padding: 4px 12px;
        font-weight: 400;
        font-size: 1rem;
        svg {
          height: 1.1rem;
          padding-top: 0.1rem;
          padding-bottom: 0.1rem;
        }
      `}
    >
      {submitting && (
        <Loading
          local
          css={css`
            position: absolute;
          `}
        />
      )}
      <div className={`${submitting && 'invisible'}`}>{children}</div>
    </Button>
  );
};

export const Bordered: React.FC<ButtonProps> = ({ className, ...props }) => {
  const btnTheme = useThemeContext().controls.button.bordered;

  return <Rounded theme={btnTheme} {...props} className={`border ${className}`} />;
};

export const Primary: React.FC<
  ButtonProps & { ewebinar?: { themeSettings?: ThemeSettingsFragment } }
> = ({ inverted, ewebinar, children, ...props }) => {
  const bgColor = inverted ? '#fff' : ewebinar?.themeSettings?.primaryColor || theme.colors.brand;
  const color = readableColorOnBackground(bgColor);
  return (
    <Rounded
      {...props}
      theme={{
        fg: color,
        fgHover: color,
        bg: bgColor,
        bgHover: bgColor,
      }}
    >
      {children}
    </Rounded>
  );
};

export const Cancel: React.FC<ButtonProps> = ({ children, ...props }) => {
  return (
    <Button
      {...props}
      css={css`
        ${tw`text-gray-1`}
      `}
    >
      {children}
    </Button>
  );
};

export const CircularDeleteButton: React.FC<ButtonProps> = ({
  onClick,
  className = '',
  ...props
}) => {
  const etheme = useThemeContext();
  const theme = props.theme ?? etheme.controls.button.delete;

  return (
    <Button
      onClick={(e): void => {
        e.stopPropagation();
        e.preventDefault();
        onClick && onClick(e);
      }}
      theme={theme}
      className={className}
      css={css`
        ${tw`focus:outline-none top-0 right-0 rounded-full items-center justify-center z-10`}
        width: 1.5rem;
        height: 1.5rem;
        min-width: 1.5rem;
        min-height: 1.5rem;
      `}
      {...props}
    >
      <CloseIcon className='w-2 h-2' />
    </Button>
  );
};

export const CircularAddButton: React.FC<ButtonProps> = ({ onClick, className = '', ...props }) => {
  const etheme = useThemeContext();
  const theme = props.theme ?? etheme.controls.button.add;

  return (
    <Button
      onClick={(e): void => {
        e.stopPropagation();
        e.preventDefault();
        onClick && onClick(e);
      }}
      theme={theme}
      className={className}
      css={css`
        ${tw`focus:outline-none hover:shadow-md hover:shadow top-0 right-0 rounded-full w-6 h-6 items-center justify-center z-1`}
      `}
      {...props}
    >
      <PlusIcon className='w-3 h-3' />
    </Button>
  );
};

export const ModalDeleteButton: React.FC<{
  className?: string;
  submitting?: boolean;
  confirmClick?: boolean;
  ConfirmModal?: JSX.Element;
  onClick: () => Promise<void>;
}> = ({ onClick, ConfirmModal, confirmClick, submitting, className }) => {
  const etheme = useThemeContext();
  const theme = etheme.controls.button.delete;
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);

  const onClickDelete = (): void => {
    if (!ConfirmModal) {
      onClick();
      return;
    }

    // Show modal
    setShowConfirmModal(true);
  };

  return (
    <>
      {showConfirmModal && ConfirmModal && (
        <Portal containerId='modalContainer'>
          {React.cloneElement(ConfirmModal, {
            onClose: () => {
              console.debug('onclose');
              setShowConfirmModal(false);
            },
          })}
        </Portal>
      )}

      <Popup
        content={
          confirmClick ? (
            <Rounded theme={etheme.controls.button.confirm} onClick={onClickDelete}>
              Confirm
            </Rounded>
          ) : (
            <span>Delete</span>
          )
        }
        on={confirmClick ? 'click' : 'hover'}
        closeOnTriggerClick={Boolean(confirmClick)}
        trigger={
          submitting ? (
            <div className='relative'>
              <Spinner
                className={className}
                css={css`
                  color: ${theme.bg};
                `}
              />
            </div>
          ) : (
            <IconButton
              id='btnDeleteInteraction'
              type='button'
              onClick={confirmClick ? undefined : onClickDelete}
              icon={
                <TrashIcon
                  className={`relative ${className}`}
                  css={css`
                    color: ${theme.bg};
                  `}
                />
              }
            />
          )
        }
        popperModifiers={[
          {
            name: 'preventOverflow',
            enabled: false,
            options: {
              boundary: 'window',
            },
          },
        ]}
      />
    </>
  );
};

export const FloatingRemoveButton: React.FC<ButtonProps & { floatLeft?: boolean }> = ({
  onClick,
  className,
  floatLeft,
}) => {
  const etheme = useThemeContext();
  const theme = etheme.controls.button.delete;

  return (
    <IconButton
      className={`
        flex rounded-full w-6 h-6
        items-center justify-center text-xs ${className}`}
      css={css`
        position: absolute;
        z-index: 9;
        ${floatLeft ? 'left: -0.75rem;' : 'right: -0.75rem;'}
        top: -0.75rem;
      `}
      theme={theme}
      onClick={(e): void => {
        e.stopPropagation();
        e.preventDefault();
        onClick && onClick(e);
      }}
      small
      icon={<CloseIcon className='w-2 h-2' />}
    />
  );
};

export const AddButton: React.FC<{ floatLeft?: boolean }> = ({ floatLeft }) => {
  return (
    <div
      className='cursor-pointer appearence-none shadow absolute bg-coral-1 rounded-full w-6 h-6 flex items-center justify-center text-white text-lg'
      css={css`
        position: absolute;
        ${floatLeft ? 'left: -0.75rem;' : 'right: -0.75rem;'}
        top: -0.75rem;
      `}
    >
      <PlusIcon className='text-white w-4 h-4' />
    </div>
  );
};

export default Button;
